
import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, Info } from 'lucide-react';

interface UserSearchInputProps {
  availableUsers: any[];
  selectedUsers: any[];
  onUserSelect: (user: any) => void;
  onUserRemove: (userId: string) => void;
}

const UserSearchInput = ({ availableUsers, selectedUsers, onUserSelect, onUserRemove }: UserSearchInputProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  const getUserDisplayName = (user: any) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.username || user.first_name || 'Unknown User';
  };
  
  // Filter users based on search query and exclude already selected users
  const filteredUsers = availableUsers.filter(user => {
    const isNotSelected = !selectedUsers.find(selected => selected.id === user.id);
    
    if (searchQuery === '') return isNotSelected;
    
    const query = searchQuery.toLowerCase();
    // Remove @ symbol from query if present
    const cleanQuery = query.startsWith('@') ? query.slice(1) : query;
    
    const matchesSearch = 
      user.username?.toLowerCase().includes(cleanQuery) ||
      user.first_name?.toLowerCase().includes(cleanQuery) ||
      user.last_name?.toLowerCase().includes(cleanQuery) ||
      getUserDisplayName(user).toLowerCase().includes(cleanQuery);
      
    return isNotSelected && matchesSearch;
  });

  console.log('UserSearchInput - availableUsers:', availableUsers);
  console.log('UserSearchInput - searchQuery:', searchQuery);
  console.log('UserSearchInput - filteredUsers:', filteredUsers);

  return (
    <div>
      <Label className="text-white">Add Members</Label>
      
      {/* Search Input */}
      <div className="relative mt-2">
        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by @username or real name..."
          className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 focus:border-sports-green pl-10"
        />
      </div>

      {/* Enhanced search functionality note */}
      <div className="flex items-start gap-2 mt-2 p-2 bg-green-900/20 border border-green-700/30 rounded text-xs text-green-300">
        <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
        <span>
          Search by typing @username or real name. Both search methods are supported.
        </span>
      </div>

      {/* Always show available users, even without search */}
      <div className="mt-2 max-h-40 overflow-y-auto bg-gray-700 border border-gray-600 rounded">
        {availableUsers.length === 0 ? (
          <div className="px-3 py-2 text-gray-400">
            No users available to add
          </div>
        ) : filteredUsers.length > 0 ? (
          filteredUsers.map(user => (
            <div
              key={user.id}
              className="px-3 py-2 hover:bg-gray-600 cursor-pointer text-white flex items-center justify-between"
              onClick={() => {
                console.log('Selecting user:', user);
                onUserSelect(user);
                setSearchQuery('');
              }}
            >
              <div>
                <span className="font-medium">{getUserDisplayName(user)}</span>
                {user.username && (
                  <span className="text-xs text-gray-400 ml-2">@{user.username}</span>
                )}
              </div>
            </div>
          ))
        ) : searchQuery.length >= 1 ? (
          <div className="px-3 py-2 text-gray-400">
            No users found matching "{searchQuery}"
          </div>
        ) : (
          <div className="px-3 py-2 text-gray-400">
            All available users are already selected
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSearchInput;
